import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import './styles.scss';
import { SelectDeviceProps } from './interfaces';
import { ScreenEnum } from '../../enums';
import Progress from '../../components/Progress';
import { useTranslation } from 'react-i18next';
import AppStateContext from '../../AppStateProvider/context';
import RadioButtons from '../../components/RadioButtons';
import i18next from 'i18next';
import { CheckmarkPasswordToggle } from '../../assets/Icons';
import { Password } from '../Password';
import ExplanationOverlay from '../../components/ExplanationOverlay';
import { AppState } from '../../AppStateProvider/interfaces';

type Activation = {
	id_token: string;
	refresh_token: string;
	token_type?: string;
} | null;

export default function SelectDevice(props: SelectDeviceProps) {
	const { t } = useTranslation();
	const { state, dispatch } = useContext(AppStateContext);

	const [individualOrKvps, setIndividualOrKvps] = useState(
		state.userPassword ? 'individual' : 'kvps'
	);
	const [passwordToggle, setPasswordToggle] = useState(false);
	const [title, setTitle] = useState<string>('');
	const [vin, setVin] = useState<string>('');
	const [showDeletePasswordWarning, setShowDeletePasswordWarning] =
		useState(false);
	const [status, setStatus] = useState<string>('');

	useEffect(() => {
		if (typeof state.goToLabel === 'undefined') {
			dispatch({ type: 'SET_GO_TO_LABEL', value: false });
		}
	}, []);

	const deliveryBase = useMemo(() => {
		const ep = state.market?.urls?.delivery;
		const lsDelivery = localStorage.getItem('url_delivery');
		const lsQs = localStorage.getItem('qs_url');
		let raw = ep || lsDelivery || lsQs || '';
		if (!raw) return '';
		if (/\/module\/delivery\/bpca$/i.test(raw)) return raw;
		return `${raw}/module/delivery/bpca`;
	}, [state.market?.urls?.delivery]);

	const activation: Activation = useMemo(() => {
		const idS = localStorage.getItem('id_token') || '';
		const refS = localStorage.getItem('refresh_token') || '';
		if (idS && refS)
			return { id_token: idS, refresh_token: refS, token_type: 'Bearer' };
		return null;
	}, []);

	const stopRef = useRef(false);
	const contentsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const clearContentsTimer = () => {
		if (contentsTimerRef.current) {
			clearTimeout(contentsTimerRef.current);
			contentsTimerRef.current = null;
		}
	};

	const fetchAccessTokenWithBackoff = async (): Promise<string | null> => {
		if (
			!deliveryBase ||
			!activation?.id_token ||
			!activation?.refresh_token
		) {
			setStatus('Warte auf Koppelung/URLs …');
			return null;
		}
		let delay = 3000;
		const MAX_DELAY = 15000;
		while (!stopRef.current) {
			const expiresAt = localStorage.getItem('accesstoken_expires_at');
			const isExpired = !expiresAt || Date.now() > Number(expiresAt);

			if (!isExpired && localStorage.getItem('accesstoken')) {
				console.log('token from ls');
				return localStorage.getItem('accesstoken');
			}

			const formData = new FormData();
			formData.append('client_id', activation.id_token);
			formData.append('client_secret', activation.refresh_token);
			formData.append('grant_type', 'client_credentials');
			const url = `${deliveryBase}/api/v2/oauth/token?device_id=${state.device}`;
			const resp = await fetch(url, {
				method: 'POST',
				headers: { 'X-Requested-With': 'XMLHttpRequest' },
				body: formData,
			});
			let body: any;
			try {
				body = await resp.clone().json();
			} catch {}
			if (resp.ok && body?.access_token) {
				return body.access_token as string;
			}
			if (resp.status === 404 || resp.status === 429) {
				const ra = resp.headers.get('Retry-After');
				delay =
					ra && !Number.isNaN(+ra) && +ra > 0
						? Math.min(MAX_DELAY, +ra * 1000)
						: Math.min(
								MAX_DELAY,
								Math.round(
									resp.status === 404
										? delay * 1.5
										: delay * 2
								)
							);
				await new Promise(r => setTimeout(r, delay));
				continue;
			}
			await new Promise(r => setTimeout(r, Math.min(MAX_DELAY, delay)));
		}
		return null;
	};

	const refreshAccessTokenOn401 = async (): Promise<string | null> => {
		if (
			!deliveryBase ||
			!activation?.id_token ||
			!activation?.refresh_token
		)
			return null;
		const formData = new FormData();
		formData.append('client_id', activation.id_token);
		formData.append('client_secret', activation.refresh_token);
		formData.append('grant_type', 'client_credentials');
		const resp = await fetch(
			`${deliveryBase}/api/v2/oauth/token?device_id=${state.device}`,
			{
				method: 'POST',
				headers: { 'X-Requested-With': 'XMLHttpRequest' },
				body: formData,
			}
		);
		let body: any;
		try {
			body = await resp.clone().json();
		} catch {}
		if (resp.ok && body?.access_token) {
			return body.access_token as string;
		}
		return null;
	};

	useEffect(() => {
		stopRef.current = false;
		const run = async () => {
			if (!deliveryBase) {
				setStatus('Keine Delivery-URL konfiguriert.');
				return;
			}
			if (!activation) {
				setStatus('Noch kein Gerät gekoppelt.');
				return;
			}
			let token = await fetchAccessTokenWithBackoff();
			if (!token) return;
			localStorage.setItem('accesstoken', token);
			const contentsUrl = `${deliveryBase}/api/v2/contents?fields-preset=full?device_id=${state.device}`;
			const resp = await fetch(contentsUrl, {
				method: 'GET',
				headers: {
					'X-Requested-With': 'XMLHttpRequest',
					Authorization: `Bearer ${token}`,
				},
			});
			const response = await resp.clone().json();
			const limit = response.Pagination?.Total || 1000;
			localStorage.setItem('contents_limit', limit.toString());
			const contentsUrlWithLimit = `${deliveryBase}/api/v2/contents?fields-preset=full&limit=${limit}&device_id=${state.device}`;
			let delay = 3000;
			const MAX_DELAY = 15000;
			let didRefreshOnceFor401 = false;
			const poll = async () => {
				if (stopRef.current) return;
				const resp = await fetch(contentsUrlWithLimit, {
					method: 'GET',
					headers: {
						'X-Requested-With': 'XMLHttpRequest',
						Authorization: `Bearer ${token}`,
					},
				});
				let body: any;
				try {
					body = await resp.clone().json();
				} catch {}
				if (resp.status === 200) {
					try {
						if (
							body?.RootContent &&
							Array.isArray(body?.Contents)
						) {
							const rootUid = body.RootContent;
							const rootItem = body.Contents.find(
								(it: any) => it?.Uid === rootUid
							);
							if (rootItem?.ContentFieldValues) {
								const t =
									rootItem.ContentFieldValues?.title?.[
										'de-DE'
									] ||
									rootItem.ContentFieldValues?.title?.[
										'en-GB'
									] ||
									'';
								const v =
									rootItem.ContentFieldValues?.vin || '';
								setTitle(t || '');
								setVin(v || '');
								setStatus('');
							} else {
								setStatus('Kein Content gefunden.');
							}
						} else {
							setStatus('Keine Inhalte verfügbar.');
						}
					} catch {}
					return;
				}
				if (resp.status === 204 || resp.status === 404) {
					const ra = resp.headers.get('Retry-After');
					delay =
						ra && !Number.isNaN(+ra) && +ra > 0
							? Math.min(MAX_DELAY, +ra * 1000)
							: Math.min(MAX_DELAY, delay);
					contentsTimerRef.current = setTimeout(poll, delay);
					return;
				}
				if (resp.status === 401 && !didRefreshOnceFor401) {
					didRefreshOnceFor401 = true;
					const refreshed = await refreshAccessTokenOn401();
					if (refreshed) {
						token = refreshed;
						contentsTimerRef.current = setTimeout(poll, 500);
						return;
					}
					contentsTimerRef.current = setTimeout(
						poll,
						Math.min(MAX_DELAY, delay)
					);
					return;
				}
				if (resp.status === 429) {
					const ra = resp.headers.get('Retry-After');
					delay =
						ra && !Number.isNaN(+ra) && +ra > 0
							? Math.min(MAX_DELAY, +ra * 1000)
							: Math.min(MAX_DELAY, delay * 2);
					contentsTimerRef.current = setTimeout(poll, delay);
					return;
				}
				contentsTimerRef.current = setTimeout(
					poll,
					Math.min(MAX_DELAY, delay)
				);
			};
			poll();
		};
		run();
		return () => {
			stopRef.current = true;
			clearContentsTimer();
		};
	}, [deliveryBase, activation?.id_token, activation?.refresh_token]);

	const isSubmittingRef = useRef(false);

	const goFinal = () => {
		if (isSubmittingRef.current) return;
		isSubmittingRef.current = true;

		try {
			let localStorageStateString = localStorage.getItem('state');
			let localStorageState = localStorageStateString
				? (JSON.parse(localStorageStateString) as AppState)
				: null;

			const expertMode = !!state.expertMode;

			const qsUrl = state.market?.qsUrl;
			const deliveryHost = state.market?.urls?.delivery;
			const pairingHost = state.market?.urls?.pairing;
			const vitalsHost = state.market?.urls?.vitals;

			if (expertMode) {
				localStorage.setItem('use_qs', 'true');
				if (qsUrl) {
					localStorage.setItem('qs_url', qsUrl);
					dispatch({
						type: 'SET_MARKET',
						value: {
							qsUrl: qsUrl,
							urls: undefined, // set endpoints to undefined
						},
					});
				}
			} else {
				deliveryHost &&
					localStorage.setItem('url_delivery', deliveryHost);
				pairingHost && localStorage.setItem('url_pairing', pairingHost);
				vitalsHost && localStorage.setItem('url_vitals', vitalsHost);
			}

			const newSetup: any = {
				...localStorageState,
				brand: state.brand ?? localStorageState?.brand,
				market: {
					...(state.market || localStorageState?.market || {}),
					calcDomain:
						state.market?.calcDomain ||
						localStorageState?.market?.calcDomain,
				},
				kvps:
					localStorage.getItem('kvps') ??
					localStorageState?.kvps ??
					null,
				device: state.device ?? localStorageState?.device,
				language: state.language ?? localStorageState?.language,
				label: !!state.goToLabel,
				pinpairingActivationResponse:
					state.pinpairingActivationResponse ??
					localStorageState?.pinpairingActivationResponse,
			};

			delete newSetup.use_qs;
			delete newSetup.qsUrl;
			delete newSetup.endpoints;
			delete newSetup.urls;

			localStorage.setItem('setup', JSON.stringify(newSetup));

			requestAnimationFrame(() => {
				dispatch({ type: 'SET_SCREEN', value: ScreenEnum.final });
				isSubmittingRef.current = false;
			});
		} catch (e) {
			isSubmittingRef.current = false;
		}
	};

	const individualPasswordClickHandler = () => setPasswordToggle(true);
	const kvpsPasswordClickHandler = () => {
		if (state.userPassword) setShowDeletePasswordWarning(true);
	};

	return (
		<>
			<div className='top'>
				<div
					className={`select-device ${passwordToggle ? 'opacity' : ''}`}
				>
					{props.backBtn(ScreenEnum.selectMarket)}
					<h1>{t('SelectDeviceHeadline')}</h1>
					{state.device && /^\d{3}$/.test(state.device) ? (
						<p className='p1 error-message'>
							{t('SelectDeviceError' + state.device)}
						</p>
					) : (
						<p className='p1'>{t('SelectDeviceText')}</p>
					)}
					<Progress
						screen={ScreenEnum.selectDevice}
						setScreen={props.setScreen}
					/>
					<h3>Your device will show the following pricesheet:</h3>
					{!!title && <h3>{title}</h3>}
					{!!vin && <h3>{vin}</h3>}
					{!!status && <p className='p2'>{status}</p>}
					{!state.apk && (
						<RadioButtons
							options={[
								{
									selected: state.goToLabel === false,
									text: t('SetDeviceShowPricesheet'),
									value: false,
								},
								{
									selected: state.goToLabel === true,
									text: t('SetDeviceShowLabel'),
									value: true,
								},
							]}
							onClick={(value: boolean) => {
								dispatch({ type: 'SET_GO_TO_LABEL', value });
							}}
						/>
					)}
					<div
						className={`select-password ${state.apk ? 'apk' : ''}`}
					>
						<p className='bold'>
							{i18next.t('SelectPasswordTitle')}
						</p>
						<p>
							{individualOrKvps === 'kvps'
								? i18next.t('SelectPasswordKvps')
								: i18next.t('SelectPasswordIndividual')}
						</p>
					</div>
					<div
						className={`password-toggle-wrapper ${state.apk ? 'apk' : ''}`}
					>
						<div className='password-toggle'>
							<button
								className={`${individualOrKvps === 'individual' ? 'active' : ''}`}
								type='button'
								onClick={individualPasswordClickHandler}
							>
								<div>
									<p>
										{individualOrKvps === 'individual'
											? i18next.t(
													'IndividualPasswordAsPassword'
												)
											: i18next.t('IndividualPassword')}
									</p>
									<p>
										{individualOrKvps === 'individual' &&
											CheckmarkPasswordToggle}
									</p>
								</div>
							</button>
							<button
								className={`${individualOrKvps === 'kvps' ? 'active' : ''}`}
								type='button'
								onClick={kvpsPasswordClickHandler}
							>
								<div>
									<p>
										{individualOrKvps === 'kvps'
											? i18next.t('KVPSAsPassword')
											: i18next.t('KvpsPassword')}
									</p>
									<p>
										{individualOrKvps === 'kvps' &&
											CheckmarkPasswordToggle}
									</p>
								</div>
							</button>
						</div>
					</div>
				</div>
			</div>
			{!passwordToggle && (
				<div className='buttons'>
					<button className='white' onClick={goFinal}>
						{t('ButtonConfirm')}
					</button>
					{props.cancelBtn}
				</div>
			)}
			{passwordToggle && (
				<Password
					passwordToggle={() => setPasswordToggle(false)}
					saveBtn={() => {
						setPasswordToggle(false);
						setIndividualOrKvps('individual');
					}}
					setkvps={() => setIndividualOrKvps('kvps')}
				/>
			)}
			{showDeletePasswordWarning && (
				<ExplanationOverlay
					button={() => {
						setShowDeletePasswordWarning(false);
						dispatch({ type: 'SET_USER_PASSWORD', value: null });
						dispatch({
							type: 'SET_SCREEN',
							value: ScreenEnum.selectDevice,
						});
						setIndividualOrKvps('kvps');
					}}
					cancel={() => setShowDeletePasswordWarning(false)}
					showButton={true}
					title={i18next.t('DeletePassword')}
					text={'DeletePasswordsTexts'}
				/>
			)}
		</>
	);
}
